import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Container,
  Grid,
  Typography,
  makeStyles, Button
} from '@material-ui/core';
import WarningAmberIcon from '@material-ui/icons/Warning';
import { getFlagValue } from 'src/utils/configUtil';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    paddingTop: 50,
    paddingBottom: 200,
    [theme.breakpoints.down('md')]: {
      paddingTop: 25,
      paddingBottom: 60
    }
  },
  bannerWrapper:{
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(4)
  },
  banner: {
    width: 'min(1300px, 90%)',
    padding: theme.spacing(2, 1),
    background: theme.palette.secondary ? theme.palette.secondary.main : '#D7263D',
    color: theme.palette.getContrastText ? theme.palette.getContrastText(theme.palette.secondary ? theme.palette.secondary.main : '#D7263D') : '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    fontWeight: 700,
    margin: `${theme.spacing(0)} auto ${theme.spacing(4)}`,
    borderRadius: 10,
    '& svg': {
      fontSize: 48,
      margin: theme.spacing(0, 1.5),
    },
    '& p': {
      fontSize: '1.125rem',
      lineHeight: 1.25,
      margin: 0
    }
  },
  image: {
    perspectiveOrigin: 'left center',
    transformStyle: 'preserve-3d',
    perspective: 1500,
    '& > img': {
      maxWidth: '90%',
      height: 'auto',
      transform: 'rotateY(-35deg) rotateX(15deg)',
      backfaceVisibility: 'hidden',
      boxShadow: theme.shadows[16]
    }
  },
  shape: {
    position: 'absolute',
    top: 0,
    left: 0,
    '& > img': {
      maxWidth: '90%',
      height: 'auto'
    }
  }
}));

function Hero({ className, ...rest }) {
  const classes = useStyles();
  const isMaintenanceEnabled = getFlagValue(process.env.REACT_APP_MAINTENANCE_ENABLED ?? '');
  
  return (
    <div
      className={clsx(classes.root, className)}
      {...rest}
    >
      {!isMaintenanceEnabled && <Box className={classes.bannerWrapper} >
        <Box className={classes.banner} role="status" aria-live="polite">
          <Box display="flex" alignItems="center" justifyContent="center">
            <WarningAmberIcon aria-hidden="true" />
            <Typography variant="body1" component="p">
              {`Scheduled Maintenance This Weekend — We're updating our portal to bring you an enhanced experience! To implement these improvements, there will be a short maintenance period over the weekend. We appreciate your patience and understanding while we implement these changes.`}</Typography>
          </Box>
        </Box>
      </Box>}
     {isMaintenanceEnabled && (
        <Box className={classes.bannerWrapper}>
          <Box className={classes.banner} role="status" aria-live="polite" display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" justifyContent="center">
              <WarningAmberIcon aria-hidden="true" />
              <Typography variant="subtitle1" component="p" style={{ fontWeight: 700 }}>
                Scheduled Maintenance:
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="center" mt={1}>
              <Typography variant="body1" component="p">
                We’re upgrading to a faster, smarter claims experience – thank you for your patience.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Container maxWidth="lg">
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
            md={5}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              height="100%"
            >
              <Typography
                variant="overline"
                color="secondary"
              >
                Introducing
              </Typography>
              <Typography
                variant="h1"
                color="textPrimary"
              >
                Tire Rack Claims Portal
              </Typography>
              <Box mt={3}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                >
                  Welcome to the future!
                  <br/>
                  <br/>
                  Our new digital road hazard claim portal has been developed to provide our valued customers with a
                  best-in-class experience. This self-serve portal was designed to maximize the speed and security with
                  which your claim will be resolved. It is available 24/7/365 from any place and on any device! Just
                  register, log in, and follow the easy instructions. Automated alerts will keep you informed of the
                  claim status every step of the way.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={7}
          >
            <Box position="relative">
              <div className={classes.shape}>
                <img
                  alt="Shapes"
                  src="/static/home/shapes.svg"
                />
              </div>
              <div className={classes.image}>
                <img
                  alt="Presentation"
                  src="/static/home/claims.jpeg"
                />
              </div>
            </Box>
          </Grid>
          {!isMaintenanceEnabled && <Grid
            item
            xs={12}
          >
            <Typography
              variant="h1"
              color="textSecondary"
            >
              Ready to get started with managing your claims?
            </Typography>

            <Typography
              variant="h3"
              color="secondary"
            >
              Already registered
            </Typography>
            <br/>
            <Button
              color="secondary"
              component="a"
              href="/login"
              variant="contained"
            >
              Log in
            </Button>
            <br/>
            <br/>
            <Typography
              variant="h3"
              color="secondary"
            >
              Not registered yet? Click register to get started.
            </Typography>
            <br/>
            <Button
              color="secondary"
              component="a"
              href="/register"
              variant="contained"
            >
              Register
            </Button>

            {/*<Typography*/}
            {/*  variant="body1"*/}
            {/*  color="textSecondary"*/}
            {/*>*/}
            {/*  <Link to={'/login'}>Login</Link> in or <Link to={'/register'}>register</Link> to file a claim.*/}
            {/*</Typography>*/}
          </Grid>}
        </Grid>
      </Container>
    </div>
  );
}

Hero.propTypes = {
  className: PropTypes.string
};

export default Hero;
